/** All server data access. Components use these hooks, never `fetch`. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './client'
import { qk } from './keys'
import type {
  AdapterList,
  DatasetDetail,
  DatasetSummary,
  Defaults,
  Leaderboard,
  MetricsList,
  ModelDetail,
  ModelSummary,
  ModelUploadResult,
  Paged,
  PreflightResponse,
  AuthMe,
  PlatformRole,
  PlatformUser,
  ProviderList,
  RunCreateBody,
  RunDetail,
  RunEvent,
  RunSummary,
  SystemHealth,
  SystemInfo,
} from './types'
import { toQuery, type TableState } from '@/hooks/useTableState'

// --------------------------------------------------------------------- library

export function useDatasets(state: TableState) {
  return useQuery({
    queryKey: qk.datasets(state),
    queryFn: () => api.get<Paged<DatasetSummary>>('/datasets', toQuery(state)),
    staleTime: 30_000,
  })
}

export function useDataset(id: string | undefined) {
  return useQuery({
    queryKey: qk.dataset(id ?? ''),
    queryFn: () => api.get<DatasetDetail>(`/datasets/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function useModels(state: TableState) {
  return useQuery({
    queryKey: qk.models(state),
    queryFn: () => api.get<Paged<ModelSummary>>('/models', toQuery(state)),
    staleTime: 30_000,
  })
}

export function useModel(id: string | undefined) {
  return useQuery({
    queryKey: qk.model(id ?? ''),
    queryFn: () => api.get<ModelDetail>(`/models/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function useUploadModel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (form: FormData) => api.postForm<ModelUploadResult>('/models/upload', form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] })
    },
  })
}

export function useAdapters() {
  return useQuery({
    queryKey: qk.adapters(),
    queryFn: () => api.get<AdapterList>('/adapters'),
    staleTime: Infinity, // the registry only changes on deploy
  })
}

export function useMetrics(taskType?: string) {
  return useQuery({
    queryKey: qk.metrics(taskType),
    queryFn: () => api.get<MetricsList>('/metrics', taskType ? { task_type: taskType } : undefined),
    staleTime: 60_000,
  })
}

// ------------------------------------------------------------------------ runs

export function useRuns(state: TableState) {
  return useQuery({
    queryKey: qk.runs(state),
    queryFn: () => api.get<Paged<RunSummary>>('/runs', toQuery(state)),
    staleTime: 10_000,
  })
}

const TERMINAL = new Set(['complete', 'partial', 'failed', 'cancelled'])

export function useRun(id: string | undefined) {
  return useQuery({
    queryKey: qk.run(id ?? ''),
    queryFn: () => api.get<RunDetail>(`/runs/${id}`),
    enabled: Boolean(id),
    // A terminal run is immutable; an active one is polled as an SSE fallback.
    staleTime: (query) =>
      TERMINAL.has((query.state.data as RunDetail | undefined)?.status ?? '') ? Infinity : 0,
    refetchInterval: (query) => {
      const status = (query.state.data as RunDetail | undefined)?.status
      return status && !TERMINAL.has(status) ? 5_000 : false
    },
  })
}

export function useLeaderboard(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.leaderboard(id ?? ''),
    queryFn: () => api.get<Leaderboard>(`/runs/${id}/leaderboard`),
    enabled: Boolean(id) && enabled,
  })
}

export function useRunLogs(id: string | undefined) {
  return useQuery({
    queryKey: qk.runLogs(id ?? ''),
    queryFn: () => api.get<RunEvent[]>(`/runs/${id}/logs`),
    enabled: Boolean(id),
  })
}

export function useManifest(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.manifest(id ?? ''),
    queryFn: () => api.get<Record<string, unknown>>(`/runs/${id}/manifest`),
    enabled: Boolean(id) && enabled,
    staleTime: Infinity, // frozen at creation
  })
}

export function usePreflight() {
  return useMutation({
    mutationFn: (body: RunCreateBody) => api.post<PreflightResponse>('/runs/preflight', body),
  })
}

export function useCreateRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: RunCreateBody) =>
      api.post<RunDetail>('/runs', body, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
  })
}

export function useCancelRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (runId: string) => api.post<RunDetail>(`/runs/${runId}/cancel`),
    onSuccess: (run) => {
      queryClient.setQueryData(qk.run(run.id), run)
      queryClient.invalidateQueries({ queryKey: ['runs'] })
    },
  })
}

// ---------------------------------------------------------------------- system

export function useHealth() {
  return useQuery({
    queryKey: qk.health(),
    queryFn: () => api.get<SystemHealth>('/system/health'),
    refetchInterval: 30_000,
  })
}

export function useSystemInfo() {
  return useQuery({ queryKey: qk.info(), queryFn: () => api.get<SystemInfo>('/system/info') })
}

export function useProviders() {
  return useQuery({
    queryKey: qk.providers(),
    queryFn: () => api.get<ProviderList>('/system/providers'),
  })
}

export function useDefaults() {
  return useQuery({
    queryKey: qk.defaults(),
    queryFn: () => api.get<Defaults>('/system/defaults'),
    staleTime: Infinity,
  })
}

export function useMe() {
  return useQuery({
    queryKey: qk.me(),
    queryFn: () => api.get<AuthMe>('/auth/me'),
    retry: false,
  })
}

export function usePlatformUsers(enabled: boolean) {
  return useQuery({
    queryKey: qk.users(),
    queryFn: () => api.get<{ items: PlatformUser[] }>('/system/users'),
    enabled,
  })
}

export function useAssignPlatformUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      subject: string
      role: PlatformRole
      display_name?: string
      email?: string
    }) => api.put<PlatformUser>('/system/users', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.users() })
    },
  })
}
